// Weglot integration glue. Weglot translates the page's text; it is not part of
// format-locale resolution (formatters resolve via selectLocale, never Weglot
// directly). LocaleHandler is the only consumer — it observes Weglot's language
// into the `weglot` locale source and sinks the resolved language back into Weglot.

import { parseLocale } from "@/lib/locale"

// CSS class marking a subtree Weglot must not translate, configured through the Weglot dashboard.
export const NO_TRANSLATE = "no-translate"

// Per-slot toggle for what Weglot should not translate
export const noTranslate = {
  guide: {
    title: NO_TRANSLATE,
    content: NO_TRANSLATE,
  },
} as const

// Weglot's Portuguese codes: "pt-br" (our pt-BR) and "pt"; "fr-ca" for embedded
// orgs only; else the bare language.
function toWeglotLang(locale?: string, embedded = false): string {
  const { language, region } = parseLocale(locale)
  if (language === "pt") return region === "BR" ? "pt-br" : "pt"
  if (embedded && language === "fr" && region === "CA") return "fr-ca"
  return language ?? ""
}

// When a full reload is imminent (e.g. after a language change), translating the
// about-to-be-discarded page is wasted work and causes a visible double-translation
// flicker — the reload reboots Weglot fresh into the new language anyway. Latching this
// makes every subsequent setWeglotLanguage no-op until the navigation lands.
let reloadPending = false

export function suppressWeglotUntilReload(): void {
  reloadPending = true
}

// Translate the page by switching Weglot to the given locale's Weglot code. No-ops
// when Weglot isn't loaded or already shows that language.
export function setWeglotLanguage(locale?: string, embedded = false): void {
  if (reloadPending) return
  const weglotLang = toWeglotLang(locale, embedded)
  if (
    weglotLang &&
    window.Weglot &&
    weglotLang !== window.Weglot.getCurrentLang()
  ) {
    window.Weglot.switchTo(weglotLang)
  }
}
